import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, CardActions, CardContent, Typography } from '@mui/material';
import { formatNumber } from 'utils/format';
import SupplierServiceChips from './supplier-service-chips.component';
import SupplierLogo from './supplier-logo.component';
import DirectoryChainNote from './directory-chain-note.component';
import { DirectoryBrowserConfig, DirectoryLab } from './directory-browser-config';

type SupplierCardProps = {
    lab: DirectoryLab;
    config: DirectoryBrowserConfig;
    onReadMore: (lab: DirectoryLab) => void;
};

/** Under 10 km with one decimal, otherwise whole km, in the user's number format. */
const formatDistance = (km: number) => formatNumber(km, { maximumFractionDigits: km < 10 ? 1 : 0 });

/** DD-3662: one listed lab — logo, name, city, services, the start of its description. */
const SupplierCard: FC<SupplierCardProps> = ({ lab, config, onReadMore }) => {
    const { t: translate } = useTranslation(['client-directory']);
    const distance =
        lab.distanceKm === null || lab.distanceKm === undefined
            ? undefined
            : translate('client-directory:SUPPLIER_DISTANCE_KM', {
                  km: formatDistance(lab.distanceKm),
              });
    const place = [lab.city, distance].filter(Boolean).join(' · ');
    // DD-3696: the clinic profile has a short text for the card; the supplier profile has one text.
    const summary = lab.shortDescription || lab.description;
    return (
        <Card
            variant="outlined"
            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            data-test={`supplier-browser.card-${lab._id}`}
        >
            <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                    <SupplierLogo name={lab.name} logoSignedUrl={lab.logoSignedUrl} />
                    <Box minWidth={0}>
                        <Typography variant="subtitle1" fontWeight={600} noWrap title={lab.name ?? ''}>
                            {lab.name}
                        </Typography>
                        {/* DD-3698: the chain first, right under the name. */}
                        <DirectoryChainNote lab={lab} />
                        {place ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                                data-test={`supplier-browser.place-${lab._id}`}
                            >
                                {place}
                            </Typography>
                        ) : undefined}
                        {config.showOrganizationNumber && lab.organizationNumber ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                                data-test={`supplier-browser.org-number-${lab._id}`}
                            >
                                {lab.organizationNumber}
                            </Typography>
                        ) : undefined}
                    </Box>
                </Box>
                <SupplierServiceChips services={lab.services} labelKey={config.serviceLabelKey} />
                {summary || config.texts.noDescription ? (
                    <Typography
                        variant="body2"
                        color={summary ? 'text.primary' : 'text.secondary'}
                        sx={{
                            mt: 1.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            whiteSpace: 'pre-line',
                        }}
                    >
                        {summary || translate(config.texts.noDescription ?? '')}
                    </Typography>
                ) : undefined}
            </CardContent>
            <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Button onClick={() => onReadMore(lab)} data-test={`supplier-browser.read-more-${lab._id}`}>
                    {translate('client-directory:READ_MORE')}
                </Button>
                {config.renderAction(lab)}
            </CardActions>
        </Card>
    );
};

export default SupplierCard;
